import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Badge } from '../../../components/ui/badge';
import { 
  ArrowLeft, Save, FileText, User, Calendar, DollarSign, 
  Settings, Loader2, Building, Phone, Mail, Truck, AlertTriangle, Plus, Trash2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../../hooks/use-toast';
import { useBillingPolicy } from '../../../hooks/useBillingPolicy';
import { VEHICLE_TYPES } from '../../../constants/vehicleTypes';
import { DEFAULT_MATRIX } from '../../../constants/chargePostes';
import ChargeMatrixEditor from '../../../components/contracts/ChargeMatrixEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Valeurs acceptées par l'API (enum FuelPolicy côté backend)
const FUEL_POLICIES = ['included', 'excluded', 'advance_with_receipts'];

const ContractEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contract, setContract] = useState(null);
  const [canEdit, setCanEdit] = useState(true);
  const [editableFields, setEditableFields] = useState({
    client: true,
    dates: true,
    billing: true,
    options: true
  });
  
  // Statuts qui permettent la modification
  const MODIFIABLE_STATUSES = ['draft', 'active'];
  // Statuts avec modifications limitées
  const LIMITED_EDIT_STATUSES = ['suspended', 'pending_renewal'];

  // Fleet items pour mode fleet_location
  const [fleetItems, setFleetItems] = useState([
    { vehicleType: '', vehicleTypeLabel: '', quantity: 1, monthlyRatePerTruck: 0 }
  ]);

  // Matrice de prise en charge des charges (PM Spec 21/06/2026)
  const [chargeMatrix, setChargeMatrix] = useState(DEFAULT_MATRIX);

  const fleetTotal = fleetItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.monthlyRatePerTruck || 0), 0
  );

  const addFleetItem = () => setFleetItems(prev => [
    ...prev,
    { vehicleType: '', vehicleTypeLabel: '', quantity: 1, monthlyRatePerTruck: 0 }
  ]);

  const removeFleetItem = (index) => setFleetItems(prev => prev.filter((_, i) => i !== index));

  const updateFleetItem = (index, field, value) => {
    setFleetItems(prev => {
      const updated = [...prev];
      if (field === 'vehicleType') {
        const found = VEHICLE_TYPES.find(v => v.value === value);
        updated[index] = {
          ...updated[index],
          vehicleType: value,
          vehicleTypeLabel: found ? found.label : value,
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  };
  
  // Form data
  const billingPolicy = useBillingPolicy();
  const vatBlocked = !!billingPolicy?.vatBlocked;
  const [formData, setFormData] = useState({
    clientName: '',
    clientContact: { name: '', phone: '', email: '' },
    startDate: '',
    endDate: '',
    autoRenew: false,
    terminology: 'transfert',
    billingMode: 'per_unit',
    monthlyFixedPrice: '',
    monthlyFixedDescription: '',
    pricePerUnit: '',
    pricePerTon: '',
    applyTVA: false,
    tauxTVA: 18,
    fuelPolicy: 'included',
    autoGenerateInvoice: false,
    autoGenerateDay: 1,
    sendInvoiceByEmail: true,
    showDetailedLines: false,
    notes: ''
  });

  // Charger le contrat
  const fetchContract = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}`);
      const c = response.data;
      setContract(c);
      
      // Remplir le formulaire
      setFormData({
        clientName: c.clientName || '',
        clientContact: c.clientContact || { name: '', phone: '', email: '' },
        startDate: c.startDate ? c.startDate.split('T')[0] : '',
        endDate: c.endDate ? c.endDate.split('T')[0] : '',
        autoRenew: c.autoRenew ?? c.autoRenewal ?? false,
        terminology: c.terminology || 'transfert',
        billingMode: c.billingRules?.billingMode || 'per_unit',
        monthlyFixedPrice: c.billingRules?.monthlyFixedPrice || '',
        monthlyFixedDescription: c.billingRules?.monthlyFixedDescription || '',
        pricePerUnit: c.billingRules?.pricePerUnit || '',
        pricePerTon: c.billingRules?.pricePerTon || '',
        applyTVA: c.billingRules?.applyTVA || false,
        tauxTVA: c.billingRules?.tauxTVA || 18,
        fuelPolicy: FUEL_POLICIES.includes(c.billingRules?.fuelPolicy)
          ? c.billingRules.fuelPolicy
          : (c.billingRules?.fuelPolicy ? 'excluded' : 'included'),
        autoGenerateInvoice: c.billingRules?.autoGenerateInvoice || false,
        autoGenerateDay: c.billingRules?.autoGenerateDay || 1,
        sendInvoiceByEmail: c.billingRules?.sendInvoiceByEmail !== false,
        showDetailedLines: c.invoiceDisplayOptions?.showDetailedLines ?? c.billingRules?.showDetailedLines ?? false,
        notes: c.notes || ''
      });
      setChargeMatrix({
        ...DEFAULT_MATRIX,
        ...(c.billingRules?.fuelPolicy === 'excluded' ? { fuel: 'client' } : {}),
        ...(c.chargeMatrix || {}),
      });
      
      // Charger les fleet items si mode fleet_location
      if (c.billingRules?.billingMode === 'fleet_location' && c.billingRules?.fleetItems?.length > 0) {
        setFleetItems(c.billingRules.fleetItems);      }

      // Vérifier les permissions de modification selon le statut
      const status = c.status || 'draft';
      if (!MODIFIABLE_STATUSES.includes(status) && !LIMITED_EDIT_STATUSES.includes(status)) {
        setCanEdit(false);
        toast({
          title: "Contrat non modifiable",
          description: `Ce contrat est au statut "${status}" et ne peut plus être modifié.`,
          variant: "destructive"
        });
      } else if (LIMITED_EDIT_STATUSES.includes(status)) {
        // Modifications limitées pour les contrats suspendus
        setEditableFields({
          client: false,
          dates: true,
          billing: false,
          options: true
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger le contrat",
        variant: "destructive"
      });
      navigate('/admin/contracts');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateClientContact = (key, value) => {
    setFormData(prev => ({
      ...prev,
      clientContact: { ...prev.clientContact, [key]: value }
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        clientName: formData.clientName,
        clientContact: formData.clientContact,
        startDate: formData.startDate,
        endDate: formData.endDate,
        autoRenew: formData.autoRenew,
        terminology: formData.terminology,
        notes: formData.notes,
        chargeMatrix: (() => {
          const m = { ...chargeMatrix };
          delete m.truck_rental; delete m.updatedAt; delete m.updatedBy; delete m.notes;
          return m;
        })(),
        invoiceDisplayOptions: {
          showDetailedLines: formData.showDetailedLines
        },
        billingRules: {
          billingMode: formData.billingMode,
          monthlyFixedPrice: formData.monthlyFixedPrice ? parseFloat(formData.monthlyFixedPrice) : null,
          monthlyFixedDescription: formData.monthlyFixedDescription,
          pricePerUnit: formData.pricePerUnit ? parseFloat(formData.pricePerUnit) : null,
          pricePerTon: formData.pricePerTon ? parseFloat(formData.pricePerTon) : null,
          applyTVA: formData.applyTVA,
          tauxTVA: formData.tauxTVA,
          fuelPolicy: formData.fuelPolicy,
          autoGenerateInvoice: formData.autoGenerateInvoice,
          autoGenerateDay: parseInt(formData.autoGenerateDay) || 1,
          sendInvoiceByEmail: formData.sendInvoiceByEmail,
          ...(formData.billingMode === 'fleet_location' && {
            fleetItems: fleetItems.filter(i => i.vehicleType),
            totalMonthlyFleetAmount: fleetTotal
          })
        }
      };

      await axios.patch(`${API}/contracts/${id}`, payload);
      toast({ title: "Succès", description: "Contrat mis à jour" });
      navigate(`/admin/contracts/${id}`);
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Erreur lors de la mise à jour",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Bandeau d'avertissement si non modifiable */}
      {!canEdit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-800">Contrat non modifiable</p>
            <p className="text-sm text-red-600">
              Ce contrat est au statut "{contract?.status}" et ne peut plus être modifié.
              Contactez l'administrateur pour toute modification.
            </p>
          </div>
        </div>
      )}

      {/* Bandeau si modifications limitées */}
      {canEdit && !editableFields.client && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Modifications limitées</p>
            <p className="text-sm text-amber-600">
              Ce contrat est suspendu. Seules les dates et options peuvent être modifiées.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/admin/contracts/${id}`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-gray-900">Modifier le contrat</h1>
            <p className="text-gray-500">{contract?.contractNumber}</p>
          </div>
        </div>
        <Button onClick={handleSubmit} disabled={saving || !canEdit} data-testid="save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">
            <FileText className="w-4 h-4 mr-2" />
            Général
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <DollarSign className="w-4 h-4 mr-2" />
            Facturation
          </TabsTrigger>
          <TabsTrigger value="options" data-testid="tab-options">
            <Settings className="w-4 h-4 mr-2" />
            Options
          </TabsTrigger>
        </TabsList>

        {/* Général */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Client
                  {!editableFields.client && <Badge variant="outline" className="text-xs">Non modifiable</Badge>}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du client</Label>
                    <Input
                      value={formData.clientName}
                      onChange={(e) => updateForm('clientName', e.target.value)}
                      disabled={!canEdit || !editableFields.client}
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label>Contact</Label>
                    <Input
                      value={formData.clientContact.name}
                      onChange={(e) => updateClientContact('name', e.target.value)}
                      placeholder="Nom du contact"
                      disabled={!canEdit || !editableFields.client}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label>
                      <Phone className="w-3 h-3 inline mr-1" />
                      Téléphone
                    </Label>
                    <Input
                      value={formData.clientContact.phone}
                      onChange={(e) => updateClientContact('phone', e.target.value)}
                      disabled={!canEdit || !editableFields.client}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label>
                      <Mail className="w-3 h-3 inline mr-1" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      value={formData.clientContact.email}
                      onChange={(e) => updateClientContact('email', e.target.value)}
                      disabled={!canEdit || !editableFields.client}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
              </div>

              {/* Période */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Période du contrat
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => updateForm('startDate', e.target.value)}
                      disabled={!canEdit || !editableFields.dates}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => updateForm('endDate', e.target.value)}
                      disabled={!canEdit || !editableFields.dates}
                      data-testid="end-date-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.autoRenew}
                    onCheckedChange={(v) => updateForm('autoRenew', v)}
                    data-testid="auto-renew-switch"
                  />
                  <Label>Renouvellement automatique</Label>
                </div>
              </div>

              {/* Terminologie */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Terminologie
                </h3>
                <Select value={formData.terminology} onValueChange={(v) => updateForm('terminology', v)}>
                  <SelectTrigger data-testid="terminology-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfert">Transfert(s)</SelectItem>
                    <SelectItem value="operation">Opération(s)</SelectItem>
                    <SelectItem value="course">Course(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => updateForm('notes', e.target.value)}
                  rows={3}
                  placeholder="Notes internes sur ce contrat..."
                  data-testid="notes-textarea"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facturation */}
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Règles de facturation</CardTitle>
              <CardDescription>Configuration de la tarification et des factures</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mode de facturation */}
              <div>
                <Label>Mode de facturation</Label>
                <Select value={formData.billingMode} onValueChange={(v) => updateForm('billingMode', v)}>
                  <SelectTrigger data-testid="billing-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_unit">À l'unité (par transfert)</SelectItem>
                    <SelectItem value="per_tonnage">Au tonnage (par tonne)</SelectItem>
                    <SelectItem value="monthly_fixed">Forfait mensuel fixe</SelectItem>
                    <SelectItem value="fleet_location">Location de flotte (par camion)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.billingMode === 'monthly_fixed' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Montant forfait mensuel (FCFA)</Label>
                    <Input
                      type="number"
                      value={formData.monthlyFixedPrice}
                      onChange={(e) => updateForm('monthlyFixedPrice', e.target.value)}
                      data-testid="monthly-price-input"
                    />
                  </div>
                  <div>
                    <Label>Description du forfait</Label>
                    <Input
                      value={formData.monthlyFixedDescription}
                      onChange={(e) => updateForm('monthlyFixedDescription', e.target.value)}
                      placeholder="Ex: Location mensuelle véhicule"
                      data-testid="monthly-desc-input"
                    />
                  </div>
                </div>
              )}

              {formData.billingMode === 'per_unit' && (
                <div>
                  <Label>Prix par unité (FCFA)</Label>
                  <Input
                    type="number"
                    value={formData.pricePerUnit}
                    onChange={(e) => updateForm('pricePerUnit', e.target.value)}
                    placeholder="0"
                    data-testid="price-per-unit-input"
                  />
                </div>
              )}

              {formData.billingMode === 'per_tonnage' && (
                <div>
                  <Label>Prix par tonne (FCFA)</Label>
                  <Input
                    type="number"
                    value={formData.pricePerTon}
                    onChange={(e) => updateForm('pricePerTon', e.target.value)}
                    placeholder="5000"
                    data-testid="price-per-ton-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Le tonnage est récupéré depuis le poids net (pesée) de chaque transfert.</p>
                </div>
              )}

              {/* Fleet location builder */}
              {formData.billingMode === 'fleet_location' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold flex items-center gap-2">
                      <Truck className="w-4 h-4 text-blue-600" />
                      Composition de la flotte
                    </Label>
                    {fleetTotal > 0 && (
                      <Badge className="bg-blue-100 text-blue-800 font-semibold">
                        Total : {new Intl.NumberFormat('fr-FR').format(fleetTotal)} FCFA/mois
                      </Badge>
                    )}
                  </div>

                  {/* En-tête colonnes */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-1 bg-blue-50 rounded text-xs font-semibold text-blue-700">
                    <div className="col-span-5">Type de camion</div>
                    <div className="col-span-2 text-center">Qté</div>
                    <div className="col-span-4">Tarif/camion (FCFA)</div>
                    <div className="col-span-1" />
                  </div>

                  {fleetItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-5">
                        <Select value={item.vehicleType} onValueChange={(v) => updateFleetItem(index, 'vehicleType', v)}>
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Type de camion..." />
                          </SelectTrigger>
                          <SelectContent>
                            {VEHICLE_TYPES.map(v => (
                              <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" min="1" max="999"
                            value={item.quantity}
                            onChange={(e) => updateFleetItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="h-8 text-sm text-center"
                            placeholder="ex: 3"
                          />
                          <span className="text-xs text-gray-400 whitespace-nowrap">cam.</span>
                        </div>
                      </div>
                      <div className="col-span-4">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" min="0"
                            value={item.monthlyRatePerTruck}
                            onChange={(e) => updateFleetItem(index, 'monthlyRatePerTruck', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm"
                            placeholder="ex: 3000000"
                          />
                          <span className="text-xs text-gray-400 whitespace-nowrap">FCFA</span>
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {fleetItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                            onClick={() => removeFleetItem(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  <Button variant="outline" size="sm" onClick={addFleetItem}
                    className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter un type de camion
                  </Button>
                </div>
              )}

              {/* TVA — grisée si le régime d'imposition interdit la TVA */}
              <div className={`flex items-center justify-between p-4 border rounded-lg ${vatBlocked ? 'bg-gray-100 border-gray-200' : ''}`} data-testid="contract-tva-block">
                <div>
                  <Label className={vatBlocked ? 'text-gray-500' : ''}>
                    Appliquer la TVA
                    {vatBlocked && <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">indisponible</span>}
                  </Label>
                  <p className="text-sm text-gray-500 max-w-xl">
                    {vatBlocked ? billingPolicy.message : 'La TVA sera ajoutée aux factures'}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={formData.applyTVA && !vatBlocked}
                    disabled={vatBlocked}
                    onCheckedChange={(v) => updateForm('applyTVA', v)}
                    data-testid="apply-tva-switch"
                  />
                  {formData.applyTVA && !vatBlocked && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        className="w-20"
                        value={formData.tauxTVA}
                        onChange={(e) => updateForm('tauxTVA', parseInt(e.target.value) || 18)}
                        data-testid="tva-rate-input"
                      />
                      <span className="text-sm">%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Carburant */}
              <div>
                <Label>Politique carburant</Label>
                <Select value={formData.fuelPolicy} onValueChange={(v) => {
                  updateForm('fuelPolicy', v);
                  setChargeMatrix((m) => ({ ...m, fuel: v === 'excluded' ? 'client' : 'baobab' }));
                }}>
                  <SelectTrigger data-testid="fuel-policy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="included">Carburant inclus dans le prix</SelectItem>
                    <SelectItem value="excluded">Carburant exclu (à la charge du client)</SelectItem>
                    <SelectItem value="advance_with_receipts">Avance avec justificatifs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Matrice de prise en charge — PM Spec 21/06/2026 */}
              <div className="pt-4 border-t">
                <Label>Qui paie quoi ?</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Seuls les postes à notre charge entrent dans le calcul de la marge.
                </p>
                <ChargeMatrixEditor value={chargeMatrix} onChange={setChargeMatrix} compact />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Options */}
        <TabsContent value="options">
          <Card>
            <CardHeader>
              <CardTitle>Options avancées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auto-génération factures */}
              <div className="space-y-4 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Génération automatique de factures</Label>
                    <p className="text-sm text-gray-500">Générer automatiquement les factures chaque mois</p>
                  </div>
                  <Switch
                    checked={formData.autoGenerateInvoice}
                    onCheckedChange={(v) => updateForm('autoGenerateInvoice', v)}
                    data-testid="auto-invoice-switch"
                  />
                </div>

                {formData.autoGenerateInvoice && (
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <Label>Jour de génération</Label>
                      <Select 
                        value={String(formData.autoGenerateDay)} 
                        onValueChange={(v) => updateForm('autoGenerateDay', parseInt(v))}
                      >
                        <SelectTrigger data-testid="auto-gen-day-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 5, 10, 15, 20, 25, 28].map(day => (
                            <SelectItem key={day} value={String(day)}>Le {day} du mois</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.sendInvoiceByEmail}
                        onCheckedChange={(v) => updateForm('sendInvoiceByEmail', v)}
                        data-testid="send-email-switch"
                      />
                      <Label>Envoyer par email</Label>
                    </div>
                  </div>
                )}
              </div>

              {/* Lignes détaillées */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label>Lignes détaillées sur factures</Label>
                  <p className="text-sm text-gray-500">Afficher le détail de chaque transfert sur les factures</p>
                </div>
                <Switch
                  checked={formData.showDetailedLines}
                  onCheckedChange={(v) => updateForm('showDetailedLines', v)}
                  data-testid="detailed-lines-switch"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button (bottom) */}
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate(`/admin/contracts/${id}`)} data-testid="cancel-btn">
          Annuler
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
};

export default ContractEdit;
